import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  createAssignmentReview,
  createAssignmentRevision,
  fetchAssignmentResponseById,
} from '../../services/assignmentResponseService/assignmentResponseService';
import {
  AssignmentResponsePageContainer,
  ContentWrapper,
  Header,
  Section,
  SectionTitle,
  SectionContent,
  RatingStars,
  CommentInput,
  SectionWrapper,
  LinkContainer,
  LinkItem,
  LinkIcon,
  ResponseText,
  Footer,
  ButtonWrapper,
} from './AssignmentResponseDetailPage.styles';
import Sidebar from '../../components/Sidebar';
import { AssignmentResponseDetail } from './AssignmentResponseDetailPage.types';
import FileToDisplay from '../../components/FileToDisplay';
import Button from '../../components/Buttons/Button';
import SuccessModal from '../../components/Modals/SuccessModal';
import linkIcon from '../../assets/icons/linkIcon.svg';
import starFill from '../../assets/icons/starFill.svg';
import star from '../../assets/icons/star.svg';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { pxToRem } from '../../styles/utils';

const AssignmentResponseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [data, setData] = useState<AssignmentResponseDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [rating, setRating] = useState<number>(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalMsg, setModalMsg] = useState<string>('');
  const toast = useRef<Toast>(null);

  const isFrozen = !!data?.is_reviewed;

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchAssignmentResponseById(Number(id));
      setData(res);
      setRating(res.assignment_review?.rating || 0);
      setComment(res.assignment_review?.teacher_comments || '');
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Ошибка загрузки данных',
        life: 3000,
      });
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const clickStar = (i: number) => !isFrozen && setRating(i + 1);
  const hoverStar = (i: number) => !isFrozen && setHovered(i + 1);
  const leaveStar = () => setHovered(null);

  const doFeedback = async () => {
    if (!data || isFrozen) return;
    try {
      await createAssignmentReview({
        response: data.id,
        teacher_comments: comment,
        rating,
      });
      setModalMsg('Фидбек успешно отправлен');
      setModalVisible(true);
      await load();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: err.response?.data?.detail ?? 'Не удалось отправить фидбек',
        life: 3000,
      });
    }
  };

  const doRevision = async () => {
    if (!data || isFrozen) return;
    try {
      await createAssignmentRevision({
        response: data.id,
        teacher_comments: comment,
      });
      setModalMsg('Запрос доработки отправлен');
      setModalVisible(true);
      await load();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: err.response?.data?.detail ?? 'Не удалось отправить запрос',
        life: 3000,
      });
    }
  };

  if (loading)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <ProgressSpinner />
      </div>
    );

  if (!data) return <div>Данные не найдены</div>;

  const disableFeedbackBtn = isFrozen || rating === 0 || comment.trim() === '';
  const disableRevisionBtn = isFrozen || comment.trim() === '';

  return (
    <AssignmentResponsePageContainer>
      <Sidebar />
      <Toast ref={toast} />

      <ContentWrapper>
        <Header>
          <h1>{data.student.full_name}</h1>
          <h2>Курс: {data.course.title}</h2>
          <h2>Урок: {data.lesson.title}</h2>
          <h2>Задание: {data.assignment.title}</h2>
        </Header>

        <SectionWrapper>

          {data.files_from_storage.length > 0 && (
            <Section>
              <SectionTitle>Прикрепленные файлы</SectionTitle>
              <SectionContent>
                <FileToDisplay files_from_storage={data.files_from_storage} />
              </SectionContent>
            </Section>
          )}

          {data.video_url && (
            <Section>
              <SectionTitle>Ссылка</SectionTitle>
              <SectionContent>
                <LinkContainer as="a" href={data.video_url} target="_blank" rel="noopener noreferrer">
                  <LinkIcon src={linkIcon} alt="Ссылка" />
                  <LinkItem>{data.video_url}</LinkItem>
                </LinkContainer>
              </SectionContent>
            </Section>
          )}

          <Section>
            <SectionTitle>Ответ пользователя</SectionTitle>
            <SectionContent>
              <ResponseText>{data.text}</ResponseText>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Ответ организатора</SectionTitle>
            <CommentInput
              placeholder="Напишите комментарий ученику"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isFrozen}
            />
          </Section>

          <Section>
            <SectionTitle>Поставьте оценку</SectionTitle>
            <RatingStars>
              {[...Array(5)].map((_, idx) => (
                <img
                  key={idx}
                  src={idx < (hovered ?? rating) ? starFill : star}
                  alt={`${idx + 1} star`}
                  onClick={() => clickStar(idx)}
                  onMouseEnter={() => hoverStar(idx)}
                  onMouseLeave={leaveStar}
                  style={{
                    cursor: isFrozen ? 'default' : 'pointer',
                    width: '32px',
                    height: '32px',
                  }}
                />
              ))}
            </RatingStars>
          </Section>
        </SectionWrapper>

        <Footer>
            <ButtonWrapper>
          <Button
            variant="flatFilled"
            width={pxToRem(200)}
            height={pxToRem(50)}
            disabled={disableFeedbackBtn}
            onClick={doFeedback}
          >
            {isFrozen ? 'Ответ отправлен' : 'Ответить'}
          </Button>

          <Button
            variant="flatFilled"
            width={pxToRem(240)}
            height={pxToRem(50)}
            disabled={disableRevisionBtn}
            onClick={doRevision}
          >
            {isFrozen ? 'Отправлено на доработку' : 'Отправить на доработку'}
          </Button>
            </ButtonWrapper>
        </Footer>

        {modalVisible && <SuccessModal message={modalMsg} onClose={() => setModalVisible(false)} />}
      </ContentWrapper>
    </AssignmentResponsePageContainer>
  );
};

export default AssignmentResponseDetailPage;
